import React, { useState } from "react";
import { EditorDialog, Field, Area, ActiveSwitch, ManageList, linesToList, listToLines } from "@/components/admin/shared";
import { ImageUpload } from "@/components/ImageUpload";
import { useCrud } from "@/components/admin/useCrud";

const EMPTY = { name: "", category: "Wellness", weight: "", price: "", original_price: "", tag: "", image: "", description: "", benefits: [], ingredients: [], dosage: "", precautions: [], nutritional_facts: {}, active: true };

export const AdminProducts = ({ products, setProducts }) => {
  const c = useCrud("products", products, setProducts);
  return (
    <>
      <ManageList title="Products" prefix="admin-product" items={c.filtered} query={c.query} setQuery={c.setQuery}
        onAdd={() => c.setEditing(EMPTY)} onEdit={c.setEditing} onDelete={c.remove}
        subtitle={(p) => `${p.weight || "—"} · ₹${Number(p.price).toLocaleString("en-IN")}`} />
      {c.editing && <ProductEditor product={c.editing} onClose={() => c.setEditing(null)} onSave={c.save} saving={c.saving} />}
    </>
  );
};

const ProductEditor = ({ product, onClose, onSave, saving }) => {
  const [f, setF] = useState({
    ...product,
    benefits: listToLines(product.benefits), ingredients: listToLines(product.ingredients), precautions: listToLines(product.precautions),
    facts: Object.entries(product.nutritional_facts || {}).map(([k, v]) => `${k}: ${v}`).join("\n"),
  });
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  const submit = () => {
    const facts = {};
    linesToList(f.facts).forEach((l) => { const [k, ...v] = l.split(":"); if (k && v.length) facts[k.trim()] = v.join(":").trim(); });
    onSave({
      name: f.name, category: f.category, weight: f.weight, price: Number(f.price), original_price: f.original_price ? Number(f.original_price) : null,
      tag: f.tag, image: f.image, description: f.description, benefits: linesToList(f.benefits), ingredients: linesToList(f.ingredients),
      dosage: f.dosage, precautions: linesToList(f.precautions), nutritional_facts: facts, active: f.active,
    }, (p) => (!p.name.trim() ? "Product name is required" : !(p.price > 0) ? "Enter a valid price" : null));
  };
  return (
    <EditorDialog title={product.id ? "Edit Product" : "Add Product"} testid="product-editor" onClose={onClose} onSave={submit} saving={saving}>
      <Field label="Product name" testid="product-edit-name" value={f.name} onChange={set("name")} />
      <ImageUpload value={f.image} onChange={(v) => setF((x) => ({ ...x, image: v }))} testid="product-edit-image" />
      <div className="grid grid-cols-2 gap-2">
        <Field label="Quantity / weight" testid="product-edit-weight" value={f.weight} onChange={set("weight")} placeholder="e.g. 500 gm" />
        <Field label="Category" testid="product-edit-category" value={f.category} onChange={set("category")} />
        <Field label="Price (₹)" testid="product-edit-price" type="number" inputMode="numeric" value={f.price} onChange={set("price")} />
        <Field label="MRP / original (₹)" testid="product-edit-original" type="number" inputMode="numeric" value={f.original_price ?? ""} onChange={set("original_price")} />
      </div>
      <Field label="Tag / badge" testid="product-edit-tag" value={f.tag || ""} onChange={set("tag")} placeholder="e.g. Bestseller" />
      <Area label="Description" testid="product-edit-description" rows={3} value={f.description || ""} onChange={set("description")} />
      <Area label="Benefits (one per line)" testid="product-edit-benefits" rows={4} value={f.benefits} onChange={set("benefits")} />
      <Area label="Ingredients (one per line)" testid="product-edit-ingredients" rows={3} value={f.ingredients} onChange={set("ingredients")} />
      <Area label="How to use" testid="product-edit-dosage" rows={3} value={f.dosage} onChange={set("dosage")} />
      <Area label="Precautions (one per line)" testid="product-edit-precautions" rows={3} value={f.precautions} onChange={set("precautions")} />
      <Area label="Nutrition facts (Label: value per line)" testid="product-edit-facts" rows={4} value={f.facts} onChange={set("facts")} placeholder={"Calories: 8 kcal\nProtein: 2 g"} />
      <ActiveSwitch checked={f.active !== false} onChange={(v) => setF((x) => ({ ...x, active: v }))} testid="product-edit-active" />
    </EditorDialog>
  );
};
